import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@heroui/react';
import toast from 'react-hot-toast';
import { FaXmark, FaArrowLeft, FaArrowRight, FaBolt, FaFloppyDisk } from 'react-icons/fa6';
import { autoDubWorkflowsState } from '../../store.jsx';
import autoDubService, { defaultDraft, saveBuilderState, loadPendingBuilderState } from '../../services/autoDub/autoDubService.js';
import { CONNECT_ERRORS, workflowToDraft } from '../../services/autoDub/mappers.js';
import repositories from '../../repositories.jsx';
import { useAutoDubChannels, useAutoDubWorkflows, useAutoDubConfig, useSourcePlaylists } from '../../hooks/useAutoDubData.jsx';
import PageLoader from '../../components/PageLoader.jsx';
import Header from '../../components/Header.jsx';
import BuilderSidebar from '../../components/autoDub/BuilderSidebar.jsx';
import ConnectChannelModal from '../../components/autoDub/ConnectChannelModal.jsx';
import StepChannel from '../../components/autoDub/steps/StepChannel.jsx';
import StepVideos from '../../components/autoDub/steps/StepVideos.jsx';
import StepLanguages from '../../components/autoDub/steps/StepLanguages.jsx';
import StepGoLive from '../../components/autoDub/steps/StepGoLive.jsx';

/* The "Pick My Videos" (V3) builder: a 4-step flow that creates a workflow
   on the backend (PRD §5–9). The Google OAuth consent screen is a full-page
   redirect, so an in-progress draft is parked in sessionStorage before
   leaving and restored when ?channel_connect=... brings the user back. */
export default function AutoDubBuilder() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { id } = useParams();
	// three modes: create (no id), edit (id), duplicate (id, ?/duplicate path).
	// duplicate hydrates from a source workflow like edit, but submits as create.
	const duplicating = useLocation().pathname.endsWith('/duplicate');
	const editing = !!id && !duplicating;
	const editPath = `/auto-dub/${id}/edit`;
	const { channels, allChannels, ready: channelsReady } = useAutoDubChannels();
	useAutoDubWorkflows();
	useAutoDubConfig();
	const [, setWorkflows] = useRecoilState(autoDubWorkflowsState);

	// restore a draft parked before the OAuth redirect (lazy: no step-0 flash)
	const [restored] = useState(loadPendingBuilderState);
	// in edit mode the draft is hydrated from the workflow (unless restored from
	// an OAuth round-trip); until then it's null and we show a loader
	const [draft, setDraft] = useState(() => restored?.draft ?? ((editing || duplicating) ? null : defaultDraft()));
	const [sub, setSub] = useState(() => restored?.sub ?? 0);
	const [maxReached, setMaxReached] = useState(() => restored?.maxReached ?? ((editing || duplicating) ? 3 : 0));
	const [connectMode, setConnectMode] = useState(null); // 'source' | 'dest' | null
	const [pendingSourceId, setPendingSourceId] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const scrollRef = useRef(null);

	// fetch the workflow being edited (skip when an OAuth-parked draft is restoring)
	const editQuery = useQuery({
		queryKey: ['autoDubWorkflow', id],
		queryFn: () => repositories.autoDub.workflows.get(id),
		enabled: (editing || duplicating) && !restored,
	});
	// hydrate the draft once both the workflow and the channels (to resolve the
	// source) are available — once only. Resolves against ALL channels (reauth-
	// required ones included) and gates on query readiness, not list length —
	// otherwise editing spins forever when every channel needs reconnecting.
	useEffect(() => {
		if ((!editing && !duplicating) || draft || !editQuery.data || !channelsReady) return;
		const d = workflowToDraft(editQuery.data, allChannels);
		// duplicate is a fresh create seeded from the source — flag the copy in its
		// name and drop the source's queued-video state so the picker starts clean
		// (exclusion model: all matched videos selected by default, no locked rows).
		setDraft(duplicating
			? { ...d, name: `${d.name || 'Workflow'} (Copy)`, existingVideos: [], selectedIds: undefined, deselectedIds: [] }
			: d);
	}, [editing, duplicating, draft, editQuery.data, channelsReady, allChannels]);

	useSourcePlaylists(draft?.source?.id);

	const set = (patch) => setDraft(d => ({ ...d, ...(typeof patch === 'function' ? patch(d) : patch) }));

	const go = (i) => {
		setSub(i);
		setMaxReached(m => Math.max(m, i));
		if (scrollRef.current) scrollRef.current.scrollTop = 0;
	};

	// per-step validity (PRD §5: future steps unlock only when current is valid)
	const stepValid = useMemo(() => draft ? [
		!!draft.source && !!draft.type,
		// Step 2: backfill must keep at least one video selected; watch is rule-based.
		draft.type !== 'backfill' || (draft._selectedCount ?? 0) > 0,
		draft.langs.length > 0
			&& (draft.publishMode === 'audio_track' || draft.langs.every(c => (draft.mapping[c] || []).length > 0))
			&& draft.langs.every(c => !!draft.voiceByLang[c]),
		true,
	] : [false, false, false, false], [draft]);
	const canActivate = stepValid[0] && stepValid[2];

	// connect-channel flow — the modal redirects to Google; the result lands
	// back here as ?channel_connect=success|error
	const connect = (mode) => setConnectMode(mode);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const result = params.get('channel_connect');
		if (!result) return;

		if (result === 'success') {
			const title = params.get('title') || 'Channel';
			toast.success(`${title} connected.`);
			queryClient.invalidateQueries({ queryKey: ['channels'] });
			if (restored?.connectMode === 'source' && params.get('channel_id')) {
				setPendingSourceId(String(params.get('channel_id')));
			}
		} else {
			const reason = params.get('reason');
			toast.error(CONNECT_ERRORS[reason] || 'Could not connect the channel. Please try again.');
		}
		// Strip the query params so a refresh doesn't re-fire the toast.
		window.history.replaceState({}, '', window.location.pathname);
	}, [queryClient, restored]);

	// once channels are hydrated, auto-select the freshly connected source
	// (same logic the in-modal picker used to apply)
	useEffect(() => {
		if (!pendingSourceId) return;
		const channel = channels.find(c => c.id === pendingSourceId);
		if (!channel) return;
		set(d => ({ source: channel, name: d.name || `${channel.name} auto-dub` }));
		setPendingSourceId(null);
	}, [pendingSourceId, channels]); // eslint-disable-line react-hooks/exhaustive-deps

	const cancel = () => navigate(editing ? `/auto-dub/${id}` : '/auto-dub');

	const goLive = async () => {
		setSubmitting(true);
		try {
			if (editing) {
				const updated = await autoDubService.updateWorkflow(id, draft);
				setWorkflows(ws => ws.map(w => (w.id === updated.id ? updated : w)));
				queryClient.invalidateQueries({ queryKey: ['autoDubWorkflow', id] });
				toast.success('Changes saved.');
				navigate(`/auto-dub/${id}`);
				return;
			}
			const created = await autoDubService.createWorkflow(draft);
			setWorkflows(ws => [created, ...ws]);
			toast.success(created.review
				? 'Automation created — dubs will wait for your review.'
				: 'Automation is live! New uploads will be auto-dubbed.');
			navigate(`/auto-dub/${created.id}`);
		} catch (err) {
			toast.error(err?.response?.data?.detail
				|| (editing ? 'Could not save the changes. Please try again.' : 'Could not create the automation. Please try again.'));
			setSubmitting(false);
		}
	};

	if ((editing || duplicating) && !draft) {
		return <div className="py-16"><PageLoader /></div>;
	}

	return (
		<div className="relative -mx-3 md:-mx-6 h-screen flex flex-col">
			{/* full-bleed (-mx cancels AppLayout's px-3 md:px-6 gutters) + full page height */}
			{/* header keeps the standard gutters; the builder below is full-bleed */}
			<div className="shrink-0 px-3 md:px-6">
				<Header currentPage="auto-dub" />
			</div>
			<div className="flex flex-col md:flex-row flex-1 min-h-0">
			{/* left sidebar — vertical step list */}
			<aside className="shrink-0 md:w-[320px] md:border-r border-b md:border-b-0 border-slate-200 bg-slate-50/60 px-5 md:px-6 pt-6 pb-5 overflow-y-auto">
				<div className="mb-7 text-[15px] font-bold text-slate-800">{duplicating ? 'Duplicate automation' : editing ? 'Edit automation' : 'New automation'}</div>
				<BuilderSidebar current={sub} maxReached={maxReached} onGo={go} />
			</aside>

			{/* right content area + footer */}
			<div className="flex-1 flex flex-col min-h-0">
			<div ref={scrollRef} className="flex-1 overflow-y-auto px-1 md:px-7 py-5">
				<div key={sub} className="max-w-5xl mx-auto">
					{/* Steps get ALL channels — reauth-required ones render with a warning
					    badge and aren't selectable (the steps enforce that themselves). */}
					{sub === 0 && <StepChannel state={draft} set={set} connect={connect} channels={allChannels} editing={editing} />}
					{sub === 1 && <StepVideos state={draft} set={set} editing={editing} />}
					{sub === 2 && <StepLanguages state={draft} set={set} connect={connect} channels={allChannels} editing={editing} />}
					{sub === 3 && <StepGoLive state={draft} set={set} />}
				</div>
			</div>

			{/* footer — pinned to the bottom of the builder; the step area above scrolls */}
			<div className="shrink-0 border-t border-slate-200 bg-white px-2 md:px-7 py-3.5 flex items-center gap-3">
				<Button
					variant="bordered"
					onPress={sub === 0 ? cancel : () => go(sub - 1)}
					startContent={sub === 0 ? <FaXmark size={15} /> : <FaArrowLeft size={15} />}
				>
					{sub === 0 ? 'Cancel' : 'Back'}
				</Button>
				<div className="ml-auto flex items-center gap-4">
					<span className="text-[13px] text-slate-400">Step {sub + 1} of 4</span>
					{sub < 3 ? (
						<Button color="primary" size="lg" isDisabled={!stepValid[sub]} onPress={() => go(sub + 1)} endContent={<FaArrowRight size={15} />}>Next</Button>
					) : editing ? (
						<Button color="primary" size="lg" isDisabled={!canActivate || submitting} onPress={goLive} startContent={<FaFloppyDisk size={15} />}>Save changes</Button>
					) : (
						<Button size="lg" isDisabled={!canActivate || submitting} onPress={goLive} startContent={<FaBolt size={15} />} className="bg-youtube text-white">Go live</Button>
					)}
				</div>
			</div>
			</div>

			<ConnectChannelModal
				mode={connectMode || 'source'}
				isOpen={!!connectMode}
				returnTo={editing ? editPath : duplicating ? `/auto-dub/${id}/duplicate` : '/auto-dub/create'}
				beforeRedirect={() => saveBuilderState({ draft, sub, maxReached, connectMode })}
				onClose={() => setConnectMode(null)}
			/>
			</div>
		</div>
	);
}
